import React, {
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DatePicker as DSDatePicker,
  Icon,
  InlineMessage,
} from '@saviynt/design-system';
import cx from 'classnames';

import { FieldLabel } from '../../../../shared/components';
import {
  DATE_FORMATS,
  testIds,
  VALIDATION_TYPES,
} from '../../../../shared/constants';
import { type DateField } from '../../../../shared/types';
import {
  convertToDate,
  convertToDisplayFormat,
  convertToPickerDateFormat,
  getDefaultMaxDate,
  getDefaultMinDate,
  validateManualDateInput,
} from '../../../../shared/utils/dateFormatUtils';
import { HELPTEXT_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';
import { useResponsiveLayout } from '../../hooks';

import styles from './PreviewFields.module.css';

interface DatePickerProps {
  field: DateField;
  value?: Date | string | null;
  onChange?: (date: Date | null) => void;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isPreview?: boolean;
  isModalForm?: boolean;
}

/**
 * DatePicker component for rendering date picker fields in form preview
 */
function DatePicker({
  field,
  value = null,
  onChange,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
  isPreview = false,
  isModalForm = false,
}: DatePickerProps): ReactElement {
  const [manualInputError, setManualInputError] = useState<string | undefined>(
    undefined
  );

  const { isMobileXlargeWidth } = useResponsiveLayout();

  // Holds the current invalid text the user typed; empty string means no invalid text
  const invalidTextRef = useRef('');

  const {
    id,
    fieldName,
    label,
    placeholder,
    isReadOnly: staticReadOnly,
    helperText,
    infoTooltipText,
    advConfig,
    validations,
  } = field;

  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const isReadOnly = staticReadOnly || isReadOnlyProp || false;

  const dateFormat = advConfig?.dateFormat ?? DATE_FORMATS.MM_DD_YYYY.display;
  const isPastDateAllowed = advConfig?.isPastDateAllowed ?? true;
  const isFutureDateAllowed = advConfig?.isFutureDateAllowed ?? true;

  const minDate = convertToDate(advConfig?.minDate);
  const maxDate = convertToDate(advConfig?.maxDate);

  const convertedValue = useMemo(() => {
    if (value !== null && value !== undefined) {
      return convertToDate(value);
    }

    return null;
  }, [value]);

  const pickerDateFormat = convertToPickerDateFormat(dateFormat);
  const componentKey = `preview-${id}-${dateFormat}`;
  const inputDomId = `ff-date-input-${fieldName}`;

  // Manual input format error takes priority; external error (e.g. required) shows only when field is empty
  const displayError = manualInputError ?? error;

  useEffect(() => {
    const input = document.getElementById(
      inputDomId
    ) as HTMLInputElement | null;

    if (!input) {
      return undefined;
    }

    const onInput = (e: Event) => {
      // Only process genuine user keystrokes
      if (!(e as InputEvent).isTrusted) {
        return;
      }

      const text = input.value;

      if (!text.trim()) {
        invalidTextRef.current = '';
        setManualInputError(undefined);

        return;
      }

      if (!validateManualDateInput(text, pickerDateFormat)) {
        invalidTextRef.current = text;
        setManualInputError(
          `Please enter a valid date in ${dateFormat} format`
        );
      } else {
        invalidTextRef.current = '';
        setManualInputError(undefined);
      }
    };

    input.addEventListener('input', onInput);

    return () => input.removeEventListener('input', onInput);
  }, [inputDomId, pickerDateFormat, dateFormat]);

  // Calculate effective min/max dates based on settings
  const today = new Date();
  let effectiveMinDate = minDate;
  let effectiveMaxDate = maxDate;

  if (!isPastDateAllowed) {
    effectiveMinDate = minDate && minDate > today ? minDate : today;
  }

  if (!isFutureDateAllowed) {
    effectiveMaxDate = maxDate && maxDate < today ? maxDate : today;
  }

  if (!effectiveMinDate) {
    effectiveMinDate = isPastDateAllowed ? getDefaultMinDate() : today;
  }

  if (!effectiveMaxDate) {
    effectiveMaxDate = isFutureDateAllowed ? getDefaultMaxDate() : today;
  }

  return (
    <DSDatePicker
      key={componentKey}
      inputId={inputDomId}
      inputName={fieldName}
      kind='dateStart'
      dateFormat={pickerDateFormat}
      inputSize='medium'
      inputLabel={
        <FieldLabel
          label={label}
          htmlFor={fieldName}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
        />
      }
      value={convertedValue}
      onChange={(date: Date | null) => {
        if (!date) {
          // Field cleared (reset button or blur on empty) — clear format error so required error shows
          invalidTextRef.current = '';
          setManualInputError(undefined);
          onChange?.(null);

          return;
        }

        // Read the actual input text right now to decide if this is a smart-format commit
        const input = document.getElementById(
          inputDomId
        ) as HTMLInputElement | null;
        const currentText = input?.value ?? '';

        // If the input still shows invalid text, DS smart-formatted it — reject
        if (invalidTextRef.current && currentText === invalidTextRef.current) {
          return;
        }

        // Calendar pick or valid typed date — clear error and accept
        invalidTextRef.current = '';
        setManualInputError(undefined);
        onChange?.(date);
      }}
      maxDate={effectiveMaxDate}
      minDate={effectiveMinDate}
      isRequired={isRequired}
      isDisabled={isReadOnly}
      dropdownZIndex={1300}
      tabIndex={0}
      placeholder={
        placeholder && placeholder.length > 0
          ? placeholder
          : convertToDisplayFormat(pickerDateFormat)
      }
      isCritical={!!displayError}
      CriticalHelperText={
        <InlineMessage
          text={displayError}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      }
      HelperText={
        !displayError && helperText ? (
          <InlineMessage
            text={helperText.slice(0, HELPTEXT_VALIDATION.MAX_LENGTH)}
            colorTheme='neutral'
            size='small'
          />
        ) : undefined
      }
      isMobileWidth={isMobileXlargeWidth}
      // Modals need the overflow to not be hidden. Pages do need the overflow to be hidden.
      isPortalEnabled={isModalForm || isPreview}
      classNames={{
        rootClassName: styles.datePickerRoot,
        triggerClassName: cx(isReadOnly && styles.value__isReadOnly),
      }}
      dataTestId={testIds.FF_PREVIEW_DATE_PICKER_FIELD}
    />
  );
}

export default DatePicker;
